import { isCheckBasicCampaignDiscountActive } from 'core/service/basic-plan/basic-plan'
import _ from 'lodash'
import { get } from 'lodash/fp'
import { delay } from 'redux-saga'
import { put, select, takeLatest, call, take } from 'redux-saga/effects'

import VALUES from 'core/data-model/constants/values'
import DEFAULTS from 'core/data-model/constants/defaults'

import { validateSaleInvestmentPermission, validateUserLicense } from 'core/service/distributor'
import AuthApi from 'core/service/distributor/auth-api'
import { getSelfAdvisor } from 'identity/saga'
import { isAgeAllowed } from 'core/service/rider/rider'
import { getServiceWrapper } from 'core/service-wrapper'
import { getAppConfig } from 'deploy-env/app-config'
import { setPositionCode, setRegistration } from 'identity/actions'
import { isLoggedIn, getIdentityUser, getUserAgentType } from 'identity/selectors'
import { loginRequest } from 'quick-quote/actions'
import { fnaPath, PATH } from '../../quick-quote/constants/path'
import { getToggles } from 'quick-quote/feature-toggles'
import { setFnaValue } from 'quick-quote/fna/actions'
import { getMarketConductQuestionsType } from 'quick-quote/fna/need-and-gap-analysis/selectors'
import { getSuitabilityStatus, getSuitabilityQuestionaire } from 'quick-quote/fna/suitability/selectors'
import {
  clearPayerInformation,
  clearPayerNeeded,
  selectPayerRelation,
  selectGender,
  updateBirthdate,
} from 'quick-quote/insured-information/actions'
import { getPayerRelation, getAge, getInsuredOccupationClass } from 'quick-quote/insured-information/selectors'
import { updateWealthSourceData } from 'quick-quote/my-wealth-plus/actions'
import { getWealthSource } from 'quick-quote/my-wealth-plus/selectors'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import { UPDATE_AVAILABLE_RIDERS } from 'quick-quote/product-common/coverage-plan/actions'
import {
  allowProceedOrdinary,
  salePermissionValidationCompleted as saleOrdinaryPermissionValidationCompleted,
  validatingSalePermission as validatingOrdinarySalePermission,
  rejectOrdinaryPermission,
} from 'quick-quote/product-common/validate-permission/actions'
import { updateRegularPremium, updateSumAssured } from 'quick-quote/product-investment/coverage-plan/actions'
import {
  allowProceedInvestment,
  salePermissionValidationCompleted,
  validatingSalePermission,
  rejectInvestmentPermission,
} from 'quick-quote/product-investment/validate-permission/actions'
import { getIsUserOnline } from 'quick-quote/selectors'
import { setAddingInvestmentQuestionForNonILPNeedType } from 'quick-quote/ui/actions'
import { getIsAddingInvestmentQuestionForNonILPNeedType } from 'quick-quote/ui/selectors'

import {
  VALIDATE_INVESTMENT_PRODUCT,
  VALIDATE_ORDINARY_PRODUCT,
  VALIDATE_NORMAL_PRODUCT,
  SELECT_PRODUCT,
  selectedProduct,
} from './actions'
import { getDisplayProducts, getSelectedDisplayProductCode } from './selectors'
import { getQuestionareSuitability } from '../../core/service-wrapper/partner-api/service'
import { updateSuitabilityQuestionaire } from '../../quick-quote/fna/suitability/actions'

const NO_INTERNET = { isValid: false, type: 'NoInternet' }

function* _clearWealthSourceData() {
  const wealthSource = yield select(getWealthSource)

  if (wealthSource == 'MY_WEALTH_PLUS') {
    // get wealthSource if = MY_WEALTH_PLUS should clear all default data
    yield put(
      updateBirthdate('', [], {
        value: 30,
        unit: 'year',
      })
    ) // Init birth date value
    yield put(selectGender('M')) // Init gender value
    yield put(updateRegularPremium(0, [])) // Init rpp value
    yield put(updateSumAssured(0, [])) // Init sum assured value
  }

  yield put(
    updateWealthSourceData({
      dataSource: 'DEFAULT',
      isValidSalePermission: false,
      isOpenedPDF: false,
      isOpenedLSTUPDF: false,
    })
  )
}

export function* validatePermission(action) {
  const isUserOnline = yield select(getIsUserOnline)
  if (!isUserOnline) return NO_INTERNET

  const isUserLoggedIn = yield select(isLoggedIn)
  if (!isUserLoggedIn) {
    return { isValid: false, type: 'LoginRequired' }
  }

  const serviceWrapper = getServiceWrapper()
  const selectedDisplayProduct = action.payload
  if (serviceWrapper.isProductUseLegacy(selectedDisplayProduct)) {
    try {
      const user = yield select(getIdentityUser)
      const userInfo = yield call(getSelfAdvisor)
      if (!!userInfo) {
        yield put(setPositionCode(userInfo.party.positionCode))
        yield put(setRegistration(userInfo.party.registrations))
      }
      return yield call(validateSaleInvestmentPermission, selectedDisplayProduct, userInfo, user)
    } catch (error) {
      console.error('error', error)
      yield put(setPositionCode(null))
      yield put(setRegistration(null))
      return NO_INTERNET
    }
  } else {
    yield delay(10)
    if (selectedDisplayProduct.rightToSell === 'Y') {
      return { isValid: true, type: 'VALID' }
    } else {
      return { isValid: false, type: 'INVALID_RIGHT_LICENSES' }
    }
  }
}

export function* validateOrdinaryPermission(action) {
  const isUserOnline = yield select(getIsUserOnline)
  if (!isUserOnline) return NO_INTERNET

  const isUserLoggedIn = yield select(isLoggedIn)
  if (!isUserLoggedIn) {
    return { isValid: false, type: 'LoginRequired' }
  }

  const serviceWrapper = getServiceWrapper()
  const selectedDisplayProduct = action.payload
  if (serviceWrapper.isProductUseLegacy(selectedDisplayProduct)) {
    try {
      const user = yield select(getIdentityUser)
      const userInfo = yield call(getSelfAdvisor)
      if (!!userInfo) {
        yield put(setPositionCode(userInfo.party.positionCode))
        yield put(setRegistration(userInfo.party.registrations))
      }
      return yield call(validateUserLicense, selectedDisplayProduct, userInfo, user)
    } catch (error) {
      yield put(setPositionCode(null))
      yield put(setRegistration(null))
      return NO_INTERNET
    }
  } else {
    yield delay(10)
    if (selectedDisplayProduct.rightToSell === 'Y') {
      return { isValid: true, type: 'VALID' }
    } else {
      return { isValid: false, type: 'INVALID_RIGHT_LICENSES' }
    }
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param history {ReturnType<import('history').createBrowserHistory>}
 * @param action
 */
export function* validateInvestmentSalePermission(serviceWrapper, history, action) {
  const { VALIDATE_IWEALTHY_SALE_PERMISSION } = yield call(getToggles)
  const { displayProduct, link } = action.payload

  const _internal_call = function*() {
    const selectedProductCode = yield select(getSelectedDisplayProductCode)
    if (selectedProductCode !== displayProduct.code) {
      const payerRelation = yield select(getPayerRelation)
      if (payerRelation === DEFAULTS.SPOUSE && displayProduct.payerSelection.length === 0) {
        yield put(clearPayerInformation())
        const insuredAge = yield select(getAge)
        if (insuredAge.value > 19) {
          yield put(clearPayerNeeded())
        } else {
          yield put(selectPayerRelation(DEFAULTS.PARENT))
        }
      }

      yield call(_clearWealthSourceData)
      const isRunProcessBasicCampaignDiscount = yield chkPropsBasicCampaignDiscount(serviceWrapper, {
        payload: displayProduct,
      })
      if (isRunProcessBasicCampaignDiscount) {
        yield handleBasicPlanCampaignWaivePremiumActive(serviceWrapper, { payload: displayProduct })
      } else {
        const payloadAfterValidate = yield findCorrectProductPayload(displayProduct)
        yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadAfterValidate)
      }
    }

    yield put(allowProceedInvestment())
    history.push(link)
  }

  if (VALIDATE_IWEALTHY_SALE_PERMISSION) {
    yield put(validatingSalePermission())
    const result = yield validatePermission({ payload: displayProduct })
    if (!result.isValid) {
      return yield handleInvalidResult(result)
    }
    yield call(_internal_call)
    yield put(salePermissionValidationCompleted())
  } else {
    yield call(_internal_call)
  }
}

export function* getSuitabilityQuestion(history) {
  const user = yield select(getIdentityUser)
  let questionareSuitability
  try {
    questionareSuitability = yield call(getQuestionareSuitability, user, null)
  } catch (error) {
    yield put(
      setNotificationMessage({
        type: 'ConfirmModal',
        title: 'เกิดข้อผิดพลาด',
        message: `ไม่สามารถเรียก API getQuestionareSuitability ได้ กรุณาลองใหม่อีกครั้ง (${error?.message ||
          'Unknown error'})`,
      })
    )
    return
  }

  if (Array.isArray(questionareSuitability)) {
    yield put(updateSuitabilityQuestionaire(questionareSuitability))
  }

  return questionareSuitability
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param history {ReturnType<import('history').createBrowserHistory>}
 * @param action
 */
export function* validateOrdinarySalePermission(serviceWrapper, history, action) {
  const { displayProduct, link } = action.payload

  const _internal_call = function*() {
    const selectedProductCode = yield select(getSelectedDisplayProductCode)
    if (selectedProductCode !== displayProduct.code) {
      const payerRelation = yield select(getPayerRelation)
      if (payerRelation === DEFAULTS.SPOUSE && displayProduct.payerSelection.length === 0) {
        yield put(clearPayerInformation())
        const insuredAge = yield select(getAge)
        if (insuredAge.value > 19) {
          yield put(clearPayerNeeded())
        } else {
          yield put(selectPayerRelation(DEFAULTS.PARENT))
        }
      }

      yield call(_clearWealthSourceData)
      const isRunProcessBasicCampaignDiscount = yield chkPropsBasicCampaignDiscount(serviceWrapper, {
        payload: displayProduct,
      })
      if (isRunProcessBasicCampaignDiscount) {
        yield handleBasicPlanCampaignWaivePremiumActive(serviceWrapper, { payload: displayProduct })
      } else {
        const payloadAfterValidate = yield findCorrectProductPayload(displayProduct)
        yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadAfterValidate)
      }
    }

    yield put(allowProceedInvestment())

    const isFnaPath = history.location.pathname.includes('/fna')
    if (displayProduct.category === VALUES.INDEX_LINKED && isFnaPath) {
      const suitabilityStatus = yield select(getSuitabilityStatus)
      if (suitabilityStatus === true) {
        history.push(link)
      } else {
        const suitabilityQuestionaire = yield select(getSuitabilityQuestionaire)
        let questionareSuitability
        if (Array.isArray(suitabilityQuestionaire) && suitabilityQuestionaire.length > 0) {
          questionareSuitability = suitabilityQuestionaire
        } else {
          try {
            questionareSuitability = yield call(getSuitabilityQuestion, history)
          } catch (error) {
            yield put(
              setNotificationMessage({
                type: 'ConfirmModal',
                title: 'เกิดข้อผิดพลาด',
                message: `ไม่สามารถเรียก API getQuestionareSuitability ได้ กรุณาลองใหม่อีกครั้ง (${error?.message ||
                  'Unknown error'})`,
              })
            )
            return
          }

          if (!Array.isArray(questionareSuitability) || questionareSuitability.length === 0) {
            return
          }
        }

        if (Array.isArray(questionareSuitability) && questionareSuitability.length > 0) {
          yield put(
            setNotificationMessage({
              type: 'ConfirmModal',
              title: 'แบบสอบถามเพิ่มเติม',
              message: `<p>เนื่องจากท่านเลือกผลิตภัณฑ์แบบมีผลตอบแทนตามดัชนีอ้างอิง (อินเด็กซ์ ลิงค์)</p>
          <span>กรุณากดยืนยัน เพื่อตอบแบบสอบถามเพิ่มเติม</span>`,
              customOnClick: () => {
                history.push(fnaPath(`${PATH.INDEX_LINKED}${PATH.MARKET_CONDUCTS}`))
              },
            })
          )
        }
      }
    } else {
      history.push(link)
    }
  }

  if (serviceWrapper.isOrdinaryForceLoginProduct(displayProduct)) {
    yield put(validatingOrdinarySalePermission())
    const result = yield validateOrdinaryPermission({ payload: displayProduct })
    if (!result.isValid) {
      return yield handleOrdinaryInvalidResult(result)
    }
    yield call(_internal_call)
    yield put(saleOrdinaryPermissionValidationCompleted())
  } else {
    yield call(_internal_call)
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param history {ReturnType<import('history').createBrowserHistory>}
 * @param action
 */
export function* validateNormalSalePermission(serviceWrapper, history, action) {
  const { link, displayProduct, isSelected } = action.payload
  if (!isSelected) {
    const selectedProductCode = yield select(getSelectedDisplayProductCode)
    if (selectedProductCode !== action.payload.code) {
      const payerRelation = yield select(getPayerRelation)
      if (payerRelation === DEFAULTS.SPOUSE && displayProduct.payerSelection.length === 0) {
        yield put(clearPayerInformation())
        const insuredAge = yield select(getAge)
        if (insuredAge.value > 19) {
          yield put(clearPayerNeeded())
        } else {
          yield put(selectPayerRelation(DEFAULTS.PARENT))
        }
      }
    }

    yield call(_clearWealthSourceData)
    const isRunProcessBasicCampaignDiscount = yield chkPropsBasicCampaignDiscount(serviceWrapper, {
      payload: displayProduct,
    })
    if (isRunProcessBasicCampaignDiscount) {
      yield handleBasicPlanCampaignWaivePremiumActive(serviceWrapper, { payload: displayProduct })
    } else {
      const payloadAfterValidate = yield findCorrectProductPayload(displayProduct)
      yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadAfterValidate)
    }
  }

  history.push(link)
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param action {{ payload }}
 */
export function* chkPropsBasicCampaignDiscount(serviceWrapper, action) {
  const displayProducts = yield select(getDisplayProducts)
  const payloadProductSelected = _.find(displayProducts, { code: action.payload.code })
  if (payloadProductSelected.campaignDiscount) {
    return Object.keys(payloadProductSelected.campaignDiscount)?.length > 0 ? true : false
  }
  return false
}

export function* findGroupPlanCode(code) {
  if (VALUES.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6.includes(code)) {
    return VALUES.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6
  } else if (Object.values(VALUES.GLOBAL_INDEX_16_6_CODE).includes(code)) {
    return Object.values(VALUES.GLOBAL_INDEX_16_6_CODE)
  } else if (Object.values(VALUES.KOOMVER_RETIRE_GROUP).includes(code)) {
    return VALUES.KOOMVER_RETIRE_GROUP
  } else if (Object.values(VALUES.INDEX_LINKED_25_5_CODE).includes(code)) {
    return Object.values(VALUES.INDEX_LINKED_25_5_CODE)
  } else if (Object.values(VALUES.INDEX_LINKED_15_8_CODE).includes(code)) {
    return Object.values(VALUES.INDEX_LINKED_15_8_CODE)
  } else {
    return []
  }
}

export function* findCorrectProductPayload(payload) {
  const groupPlanCodeForCheck = yield findGroupPlanCode(payload.code)
  if (groupPlanCodeForCheck.length > 0) {
    const displayProducts = yield select(getDisplayProducts)
    const insuredAge = yield select(getAge)
    for (const planCode of groupPlanCodeForCheck) {
      const payloadProduct = _.find(displayProducts, { code: planCode })
      const entryAge = {
        minimumValue: payloadProduct.entryAge.minimum,
        maximumValue: payloadProduct.entryAge.maximum,
      }
      if (payloadProduct && isAgeAllowed(insuredAge.value, entryAge)) {
        return payloadProduct
      }
    }
  }
  return payload
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param action {{ payload }}
 */
export function* handleBasicPlanCampaignWaivePremiumActive(serviceWrapper, action) {
  const displayProducts = yield select(getDisplayProducts)
  const insuredOccupationClass = yield select(getInsuredOccupationClass)
  const userAgentType = yield select(getUserAgentType)
  const payloadProductSelected = _.find(displayProducts, { code: action.payload.code })
  const switchToBasicPlanCode = _.get(payloadProductSelected.campaignDiscount, 'switchToBasicPlanCode')

  const isBasicCampaignDiscountActive = isCheckBasicCampaignDiscountActive({
    basicPlan: payloadProductSelected,
    occupationFactor: insuredOccupationClass,
    userAgentType,
  })

  const payloadAfterValidate = yield findCorrectProductPayload(action.payload)
  if (switchToBasicPlanCode && isBasicCampaignDiscountActive) {
    const payloadProductSwitch = _.find(displayProducts, { code: switchToBasicPlanCode })
    if (payloadProductSwitch) {
      yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadProductSwitch)
    } else {
      yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadAfterValidate)
    }
  } else {
    yield call(serviceWrapper.products.COMMON.onSelectProduct, payloadAfterValidate)
  }
}

export function* handleInvalidResult(result) {
  const { type, isValid } = result
  if (type === 'NoInternet') {
    yield put(setNotificationMessage({ type: 'Warning', subType: 'NoInternet' }))
  } else if (type === 'LoginRequired') {
    yield put(loginRequest())
  } else {
    yield put(rejectInvestmentPermission({ type, isValid }))
  }
  yield put(salePermissionValidationCompleted())
}

export function* handleOrdinaryInvalidResult(result) {
  const { type, isValid } = result
  if (type === 'NoInternet') {
    yield put(setNotificationMessage({ type: 'Warning', subType: 'NoInternet' }))
  } else if (type === 'LoginRequired') {
    yield put(loginRequest())
  } else {
    yield put(rejectOrdinaryPermission({ type, isValid }))
  }
  yield put(saleOrdinaryPermissionValidationCompleted())
}

export function* onSelectProduct(history, action) {
  const { ENABLE_REVISE_FNA } = yield call(getToggles)
  if (ENABLE_REVISE_FNA) {
    const isAddingInvestmentQuestionForNonILPNeedType = yield select(getIsAddingInvestmentQuestionForNonILPNeedType)
    const fnaMarketConductQuestionsType = yield select(getMarketConductQuestionsType)
    const isFnaNonInvestProductNeeded = fnaMarketConductQuestionsType === 'NON-ILP'

    const selectedProductCategory = get('category')(action.payload)
    const isSelectedProductInvestmentType = selectedProductCategory === 'INVESTMENT'

    if (
      !isAddingInvestmentQuestionForNonILPNeedType &&
      isFnaNonInvestProductNeeded &&
      isSelectedProductInvestmentType
    ) {
      yield [
        put(setAddingInvestmentQuestionForNonILPNeedType(true)),
        put(
          setNotificationMessage({
            type: 'ConfirmModal',
            title: 'แบบสอบถามเพิ่มเติม',
            message: `<p>เนื่องจากท่านเลือกผลิตภัณฑ์ประกันชีวิตควบการลงทุน</p>
          <span>กรุณากดยืนยัน เพื่อตอบแบบสอบถามเพิ่มเติม</span>`,
            customOnClick: () => {
              history.replace(fnaPath(`${PATH.INVESTMENT}${PATH.MARKET_CONDUCTS}`))
            },
          })
        ),
      ]
    } else if (isFnaNonInvestProductNeeded && !isSelectedProductInvestmentType) {
      yield [
        put(
          setFnaValue({
            path: 'marketConduct.questions.useSavingIn7Years',
            value: undefined,
          })
        ),
        put(
          setFnaValue({
            path: 'marketConduct.questions.riskIsAcceptable',
            value: undefined,
          })
        ),
        put(
          setFnaValue({
            path: 'marketConduct.questions.preferPayment',
            value: undefined,
          })
        ),
        yield put(setAddingInvestmentQuestionForNonILPNeedType(false)),
      ]
    }
  }

  yield take(UPDATE_AVAILABLE_RIDERS)
  yield put(selectedProduct(action.payload))
}

/**
 * @param history
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* sagas(history, serviceWrapper) {
  yield [
    takeLatest(VALIDATE_INVESTMENT_PRODUCT, validateInvestmentSalePermission, serviceWrapper, history),
    takeLatest(VALIDATE_ORDINARY_PRODUCT, validateOrdinarySalePermission, serviceWrapper, history),
    takeLatest(VALIDATE_NORMAL_PRODUCT, validateNormalSalePermission, serviceWrapper, history),
    takeLatest(SELECT_PRODUCT, onSelectProduct, history),
  ]
}
